import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ChevronLeft, Calendar, MapPin, Globe } from 'lucide-react';
import { membershipService } from '../services/dataService';
import type { DiscoverEvent } from '../services/dataService';
import { notify } from '../utils/notifications';
import { formatEventDateRange } from '../utils/scheduleTime';

export default function DiscoverScreen() {
  const navigate = useNavigate();
  const [events, setEvents] = useState<DiscoverEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    membershipService.listDiscover()
      .then(setEvents)
      .catch(() => notify.error('Could not load public conferences'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div
      className="size-full flex flex-col overflow-y-auto"
      style={{ backgroundColor: 'var(--app-bg)' }}
    >
      {/* Header */}
      <div className="app-screen-header px-6 pt-14 pb-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 mb-4 active:opacity-70 transition-opacity"
          style={{ color: 'var(--app-text-muted)' }}
        >
          <ChevronLeft size={18} />
          <span className="text-sm">Back</span>
        </button>
        <h1 className="text-[28px] font-semibold tracking-tight" style={{ color: 'var(--app-text)' }}>
          Discover
        </h1>
        <p className="text-sm mt-1" style={{ color: 'var(--app-text-muted)' }}>
          Upcoming conferences with public previews.
        </p>
      </div>

      <div className="flex-1 px-4 pb-8">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
          </div>
        ) : events.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center px-4">
            <Globe className="w-12 h-12 mb-4" style={{ color: 'var(--app-text-subtle)' }} />
            <p className="text-lg font-medium mb-2" style={{ color: 'var(--app-text)' }}>No public conferences</p>
            <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>
              No conferences have public previews enabled at this time.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {events.map((event) => (
              <button
                key={event.id}
                onClick={() => navigate(`/event-preview/${event.id}`)}
                className="w-full text-left rounded-2xl overflow-hidden active:scale-95 transition-transform"
                style={{ backgroundColor: 'var(--app-card-bg)', border: '1px solid var(--app-border)' }}
              >
                {event.image_url && (
                  <div className="h-36 w-full overflow-hidden">
                    <img src={event.image_url} alt={event.name} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="p-4">
                  <h3 className="text-base font-semibold mb-2" style={{ color: 'var(--app-text)' }}>
                    {event.name}
                  </h3>
                  {event.description && (
                    <p className="text-sm mb-3 line-clamp-2" style={{ color: 'var(--app-text-muted)' }}>
                      {event.description}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs" style={{ color: 'var(--app-text-subtle)' }}>
                    <span className="flex items-center gap-1">
                      <Calendar size={11} />
                      {formatEventDateRange(event.date_start, event.date_end)}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin size={11} />
                      {event.location}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
